import dayjs from 'dayjs';

import utc from 'dayjs/plugin/utc';

export function formatString(...args: any[]): string {
  // If the function has no arg or doesn't have the string then return undefined
  if (!arguments.length || !args[0]) {
    return '';
  }
  // The string containing the format items (e.g. "{0}")
  // will and always has to be the first argument.
  let theString = args[0];

  // start with the second argument (i = 1)
  for (let i = 1; i < arguments.length; i++) {
    // "gm" = RegEx options for Global search (more than one instance)
    // and for Multiline search
    const regEx = new RegExp('\\{' + (i - 1) + '\\}', 'gm');
    theString = theString.replace(regEx, args[i]);
  }
  return theString;
}

export const decapitalizeString = (str: string) => str[0].toLowerCase() + str.slice(1);

dayjs.extend(utc);

export const DATE_FORMAT = 'YYYY-MM-DD';

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const ISO_DATE_PREFIX = /^(\d{4})-(\d{2})-(\d{2})T/;

function isRealCalendarDay(year: number, month: number, day: number): boolean {
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

/**
 * True when `value` is a real calendar day (`YYYY-MM-DD`) or a parseable UTC ISO instant.
 * Rejects overflow dates (`2026-02-30`), swapped formats, and free text.
 */
export function isValidPropertyDate(value: string): boolean {
  const trimmed = value.trim();
  const dateOnly = DATE_ONLY.exec(trimmed);
  if (dateOnly) {
    return isRealCalendarDay(Number(dateOnly[1]), Number(dateOnly[2]), Number(dateOnly[3]));
  }

  const isoPrefix = ISO_DATE_PREFIX.exec(trimmed);
  if (!isoPrefix) {
    return false;
  }

  if (!isRealCalendarDay(Number(isoPrefix[1]), Number(isoPrefix[2]), Number(isoPrefix[3]))) {
    return false;
  }

  return parseUtcInstantToLocalDate(trimmed) !== null;
}

export const formateDate = (inputValue: string | Date, format = DATE_FORMAT) => {
  const date = dayjs(inputValue);

  return date.isValid() ? date.format(format) : '';
};

/**
 * Inverse of `formateDate`: the API returns date properties as a UTC ISO instant (e.g.
 * "2026-01-01T00:00:00Z"). `new Date(isoValue)` preserves that instant, but reading it back with
 * local-time accessors (as `formateDate`'s `dayjs(...).format()` does) shows the UTC calendar date
 * one day early for any timezone west of UTC. Rebuilding the Date from its UTC calendar components
 * makes local-time formatting round-trip to the same day everywhere.
 */
export const parseUtcInstantToLocalDate = (isoValue: string): Date | null => {
  const parsed = new Date(isoValue);
  if (Number.isNaN(parsed.getTime())) return null;

  return new Date(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate());
};

/**
 * Turns a grid/API date (Date, ISO instant, or YYYY-MM-DD) into a dayjs for the picker.
 * Empty / unparsable values are `null` so the field stays blank instead of jumping to today.
 */
export function toPickerDayjs(value: unknown): dayjs.Dayjs | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : dayjs(value);
  }

  if (typeof value === 'string' && value !== '') {
    if (!isValidPropertyDate(value)) {
      return null;
    }

    const localDate = parseUtcInstantToLocalDate(value);
    return localDate ? dayjs(localDate) : null;
  }

  return null;
}
